/**
 * @module app.um
 */

import { autoinject, PLATFORM } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { DateTimeValue } from 'app/shared/lib/date-time';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IServiceStatusHistoryModelData {
    serviceId: string;
}

@autoinject()
export class ServiceStatusHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    public serviceId: string = '';
    protected statusHistory: UM.AuthServiceStatusHistory[] | null = null;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(model: IServiceStatusHistoryModelData): Promise<void> {
        if(!model) return;
        this.serviceId = model.serviceId;
        this.reload();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////

    public clear(): void {
        this.statusHistory = [];
    }

    public async reload(): Promise<void> {
        this.statusHistory = await this.loadStatusHistory();
    }

    protected isTatMet(statusHistory: UM.AuthServiceStatusHistory): string {
        const endTimestamp = statusHistory?.metadata?.ended?.timestamp ? new DateTimeValue(statusHistory.metadata.ended.timestamp) : null;
        const dueDateTime = statusHistory.dueDateTime ? new DateTimeValue(statusHistory.dueDateTime) : null;
        const now = new DateTimeValue(this.env.now());
        const isOverdue = dueDateTime ? (endTimestamp?.value ? endTimestamp.isAfter(dueDateTime) : now.isAfter(dueDateTime)) : false;
        const isDetermination = statusHistory?.status?.determination?.isDetermination || null;

        if(!dueDateTime) { return 'u'; }
        else if(isOverdue) { return 'n'; }
        else if(isDetermination) { return 'u'; }
        else if(!endTimestamp) { return 'u'; }
        else { return 'm' }
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadStatusHistory(): Promise<UM.AuthServiceStatusHistory[]> {
        if(!this.serviceId) { return []; }

        const includes = [
            'audit.start',
            'audit.start.user',
            'status',
            'status.reason',
            'turnAroundTime'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadServiceStatusHistory(this.serviceId, undefined, includes);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}

/**
 * Opens the service status history modal with the global dialog
 * @parem service The service to populate the modal's title
 * @param serviceId The serviceId for which to load status history
 */
export function openServiceStatusHistory(serviceDefinition: CD.Service, serviceId: string): void {
    if(!serviceDefinition.id || !serviceId) throw new Error('Service is missing or invalid');

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '11',
        title: {
            key: 'um:routes.auth.service-status-history.$title',
            replace: { code: serviceDefinition.code, description: serviceDefinition.$display }
        },
        print: false
    };

    const modelData: IServiceStatusHistoryModelData = {
        serviceId: serviceId
    };

    const modalData: IModalData = {
        componentId: PLATFORM.moduleName('app/um/components/auth/service-status-history-grid'),
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
